import { passageSectionAsset } from "../generated/passage-sections";

export interface BibleParallelPassage {
  label: string;
  target: string;
}

export interface BiblePassageSection {
  id: string;
  title: string;
  level: number;
  book: string;
  bookIndex: number;
  startChapter: number;
  startVerse: number;
  endChapter: number;
  endVerse: number;
  parallelPassages: BibleParallelPassage[];
}

interface PassageSectionPayload {
  schemaVersion: number;
  source: {
    name: string;
    sourceUrl: string;
    packageUrl: string;
    license: string;
    attribution: string;
  };
  sections: BiblePassageSection[];
}

let passageSectionPromise: Promise<PassageSectionPayload> | null = null;

async function decodeCompressedJson<T>(response: Response): Promise<T> {
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.length === 0) throw new Error("The passage guide was empty.");
  const isGzip = bytes[0] === 0x1f && bytes[1] === 0x8b;
  if (isGzip && !("DecompressionStream" in window)) {
    throw new Error("This browser cannot open the compressed passage guide.");
  }
  const jsonBytes = isGzip
    ? new Uint8Array(
        await new Response(
          new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip")),
        ).arrayBuffer(),
      )
    : bytes;
  return JSON.parse(new TextDecoder().decode(jsonBytes)) as T;
}

export function loadPassageSections(): Promise<PassageSectionPayload> {
  passageSectionPromise ??= fetch(passageSectionAsset.assetUrl)
    .then((response) => {
      if (!response.ok) throw new Error("The passage guide could not be loaded.");
      return decodeCompressedJson<PassageSectionPayload>(response);
    })
    .catch((error) => {
      passageSectionPromise = null;
      throw error;
    });
  return passageSectionPromise;
}

function normalizedSearch(value: string): string {
  return value
    .toLocaleLowerCase()
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

export function passageSectionLabel(section: BiblePassageSection): string {
  const start = `${section.book} ${section.startChapter}:${section.startVerse}`;
  if (
    section.startChapter === section.endChapter &&
    section.startVerse === section.endVerse
  ) {
    return start;
  }
  const end =
    section.startChapter === section.endChapter
      ? String(section.endVerse)
      : `${section.endChapter}:${section.endVerse}`;
  return `${start}–${end}`;
}

export function searchPassageSections(
  sections: BiblePassageSection[],
  query: string,
  limit = 12,
): BiblePassageSection[] {
  const normalized = normalizedSearch(query);
  if (!normalized) return [];
  const terms = normalized.split(" ");
  return sections
    .map((section) => {
      const title = normalizedSearch(section.title);
      const haystack = `${title} ${normalizedSearch(section.book)} ${section.startChapter}`;
      if (!terms.every((term) => haystack.includes(term))) return null;
      const score =
        (title === normalized ? 100 : 0) +
        (title.startsWith(normalized) ? 50 : 0) +
        (title.includes(normalized) ? 25 : 0) -
        section.level;
      return { section, score };
    })
    .filter((entry): entry is { section: BiblePassageSection; score: number } => Boolean(entry))
    .sort((left, right) => right.score - left.score || left.section.bookIndex - right.section.bookIndex)
    .slice(0, limit)
    .map((entry) => entry.section);
}

export function sectionsStartingInChapter(
  sections: BiblePassageSection[],
  bookIndex: number,
  chapter: number,
): BiblePassageSection[] {
  return sections.filter(
    (section) =>
      section.bookIndex === bookIndex &&
      section.startChapter === chapter,
  );
}
